import { Component, computed, effect, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { TranslatePipe } from '@cmi/addin-core';
import { Button } from '@cmi/ui';
import { map, startWith } from 'rxjs';
import { ContentProviderProperty } from '../home/home.models';

@Component({
  selector: 'cmi-property-format-form',
  imports: [ReactiveFormsModule, Button, TranslatePipe],
  templateUrl: './property-format-form.html'
})
export class PropertyFormatForm {
  public readonly property = input.required<ContentProviderProperty>();
  public readonly initialFormatString = input<string | undefined>();
  public readonly insertMode = input<boolean>(false);
  public readonly isApplying = input<boolean>(false);
  public readonly applyFormat = output<{ formatValue: string }>();

  public readonly predefinedFormats = ['dd.MM.yyyy', 'd. MMMM yyyy', 'dddd, d. MMMM yyyy'];

  public readonly form = new FormGroup({
    selectedFormat: new FormControl(''),
    customFormat: new FormControl(''),
    trueValue: new FormControl(''),
    falseValue: new FormControl(''),
    decimalPlaces: new FormControl<number | null>(null, Validators.min(0))
  });

  // Convert form control values to signals for reactive computed properties
  public readonly selectedFormatSignal = toSignal(
    this.form.controls.selectedFormat.valueChanges.pipe(
      startWith(this.form.controls.selectedFormat.value)
    ),
    { initialValue: '' }
  );
  public readonly customFormatSignal = toSignal(
    this.form.controls.customFormat.valueChanges.pipe(
      startWith(this.form.controls.customFormat.value)
    ),
    { initialValue: '' }
  );

  // Angular form validation state
  private readonly _formValidSignal = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map((status) => status === 'VALID')
    ),
    { initialValue: false }
  );

  public readonly isDateTime = computed(() => {
    const prop = this.property();
    const typeName = this._normalizeTypeName(prop?.typeName);
    return typeName === 'datetime' || typeName === 'date';
  });

  public readonly isBoolean = computed(() => {
    const prop = this.property();
    const typeName = this._normalizeTypeName(prop?.typeName);
    return typeName === 'boolean' || typeName === 'bool';
  });

  public readonly isInt32 = computed(() => {
    const prop = this.property();
    const typeName = this._normalizeTypeName(prop?.typeName);
    return typeName === 'int32' || typeName === 'numeric';
  });

  // Get current format string from form
  public readonly currentFormat = computed(() => {
    const selectedFormat = this.selectedFormatSignal();
    if (!selectedFormat) {
      return '';
    }
    if (selectedFormat === 'custom') {
      return this.customFormatSignal() || '';
    }
    return selectedFormat;
  });

  // Preview of formatted date - shows for any selected format
  public readonly datePreview = computed(() => {
    if (!this.isDateTime()) {
      return '';
    }
    const selectedFormat = this.selectedFormatSignal();
    if (!selectedFormat) {
      return '';
    }

    let format: string;
    if (selectedFormat === 'custom') {
      format = this.customFormatSignal() || '';
    } else {
      format = selectedFormat;
    }

    if (!format) {
      return '';
    }

    return this._formatDate(format);
  });

  // Button label based on insert mode
  public readonly buttonLabel = computed(() => {
    return this.insertMode() ? 'propertyFormat.insert' : 'propertyFormat.apply';
  });

  // Check if custom format is valid (only relevant for DateTime)
  public readonly isCustomFormatValid = computed(() => {
    if (!this.isDateTime()) {
      return true;
    }
    const selectedFormat = this.selectedFormatSignal();
    if (selectedFormat !== 'custom') {
      return true;
    }
    const customFormatControl = this.form.controls.customFormat;
    return !customFormatControl.invalid || !customFormatControl.touched;
  });

  public readonly isFormValid = computed(() => {
    // Check Angular form validators first
    const formValid = this._formValidSignal();

    if (this.isDateTime()) {
      // For DateTime: must have selectedFormat selected
      const selectedFormat = this.selectedFormatSignal();
      if (!selectedFormat) {
        return false;
      }
      // If custom format is selected, it must be valid (checked by Angular validators)
      if (selectedFormat === 'custom') {
        return formValid && this.form.controls.customFormat.valid;
      }
      // Predefined format is valid if form is valid
      return formValid;
    }

    if (this.isBoolean()) {
      // Boolean: no required fields, form is always valid
      return true;
    }

    if (this.isInt32()) {
      // Int32: decimalPlaces validation is handled by Angular validators (min(0))
      return formValid;
    }

    return true;
  });

  constructor() {
    // Initialize form values when property or initialFormatString changes
    effect(() => {
      const prop = this.property();
      const formatValue = this.initialFormatString();

      // Use initialFormatString if provided, otherwise use formatString from property
      const formatToUse = formatValue ?? prop?.formatString;

      if (!formatToUse || !prop) {
        return;
      }

      if (this.isDateTime()) {
        this._initializeDateTimeFormat(formatToUse);
      } else if (this.isBoolean()) {
        this._initializeBooleanFormat(formatToUse);
      } else if (this.isInt32()) {
        this._initializeInt32Format(formatToUse);
      }
    });

    // Add validator dynamically when property is available and it's DateTime type
    effect(() => {
      try {
        const prop = this.property();
        const customFormatControl = this.form.controls.customFormat;

        if (this._normalizeTypeName(prop?.typeName) === 'datetime') {
          // Add validator if not already present
          const currentValidators = customFormatControl.validator;
          const dateTimeValidator = this._dateTimeFormatValidator.bind(this);
          if (currentValidators !== dateTimeValidator) {
            customFormatControl.setValidators([dateTimeValidator]);
            customFormatControl.updateValueAndValidity({ emitEvent: false });
          }
        } else {
          // Remove validator if property is not DateTime
          customFormatControl.clearValidators();
          customFormatControl.updateValueAndValidity({ emitEvent: false });
        }
      } catch {
        // Property input not available yet, skip validator setup
        // Validator will be added once property is available
      }
    });

    // Trigger validation when selected format changes
    effect(() => {
      const selectedFormat = this.selectedFormatSignal();
      if (selectedFormat === 'custom') {
        // Trigger validation when switching to custom
        this.form.controls.customFormat.updateValueAndValidity();
      }
    });
  }

  private _initializeDateTimeFormat(formatValue: string): void {
    if (this.predefinedFormats.includes(formatValue)) {
      this.form.patchValue(
        {
          selectedFormat: formatValue,
          customFormat: ''
        },
        { emitEvent: false }
      );
    } else {
      this.form.patchValue(
        {
          selectedFormat: 'custom',
          customFormat: formatValue
        },
        { emitEvent: false }
      );
    }
  }

  private _initializeBooleanFormat(formatValue: string): void {
    const parts = formatValue.split('|');
    if (parts.length === 2) {
      this.form.patchValue(
        {
          falseValue: parts[0],
          trueValue: parts[1]
        },
        { emitEvent: false }
      );
    }
  }

  private _initializeInt32Format(formatValue: string): void {
    this.form.patchValue(
      {
        decimalPlaces: Number(formatValue)
      },
      { emitEvent: false }
    );
  }

  private _normalizeTypeName(typeName: string | null | undefined): string {
    if (!typeName) {
      return '';
    }

    let normalized = typeName.trim();

    const nullableGenericMatch = normalized.match(/^Nullable<(.+)>$/i);
    if (nullableGenericMatch) {
      normalized = nullableGenericMatch[1].trim();
    }

    const nullableClrMatch = normalized.match(/^Nullable`1\[(.+)]$/i);
    if (nullableClrMatch) {
      normalized = nullableClrMatch[1].trim();
    }

    normalized = normalized.replace(/^System\./i, '').replace(/^Edm\./i, '');

    return normalized.toLowerCase();
  }

  public onCustomFormatFocus(): void {
    this.form.controls.selectedFormat.setValue('custom');
  }

  /**
   * Validates DateTime format string
   * Valid format specifiers: d, dd, dddd, M, MM, MMM, MMMM, y, yy, yyyy (case-insensitive)
   * Other characters are treated as literals
   */
  private _dateTimeFormatValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;

    // Only validate if custom format is selected
    const selectedFormat = this.form.controls.selectedFormat.value;
    if (selectedFormat !== 'custom') {
      return null;
    }

    if (!value || !value.trim()) {
      return null; // Empty is valid (will be handled by required check if needed)
    }

    // Remove all valid format specifiers and literals (non-letter characters)
    // What remains should be empty or only invalid format specifiers
    let remaining = value;

    // Remove valid format specifiers (case-insensitive)
    // dddd (must be exactly 4 d's)
    remaining = remaining.replace(/dddd/gi, '');
    // ddd (3 d's - not standard but we'll allow it)
    remaining = remaining.replace(/ddd/gi, '');
    // dd (2 d's)
    remaining = remaining.replace(/dd/gi, '');
    // d (single d)
    remaining = remaining.replace(/d/gi, '');

    // MMMM (4 M's)
    remaining = remaining.replace(/MMMM/gi, '');
    // MMM (3 M's)
    remaining = remaining.replace(/MMM/gi, '');
    // MM (2 M's)
    remaining = remaining.replace(/MM/gi, '');
    // M (single M)
    remaining = remaining.replace(/M/gi, '');

    // yyyy (4 y's)
    remaining = remaining.replace(/yyyy/gi, '');
    // yy (2 y's)
    remaining = remaining.replace(/yy/gi, '');
    // y (single y)
    remaining = remaining.replace(/y/gi, '');

    // Remove all non-letter characters (literals like punctuation, spaces)
    remaining = remaining.replace(/[^a-zA-Z]/g, '');

    // If anything remains, it's an invalid format specifier
    if (remaining.length > 0) {
      return { invalidFormat: true };
    }

    return null;
  }

  /**
   * Formats a date according to .NET-style format string
   * Supports: d, dd, M, MM, MMM, MMMM, y, yy, yyyy, dddd (case-insensitive)
   */
  private _formatDate(format: string): string {
    if (!format) {
      return '';
    }

    try {
      const now = new Date();
      const day = now.getDate();
      const month = now.getMonth();
      const year = now.getFullYear();

      // German locale for month/day names
      const locale = 'de-DE';

      // Use placeholder approach to avoid token conflicts
      // Use format that doesn't contain d, M, or y to avoid conflicts
      const placeholders: Record<string, string> = {};
      let placeholderIndex = 0;
      let result = format;

      // Replace tokens with placeholders, processing longest patterns first (case-insensitive)
      // Day of week (dddd = full name)
      result = result.replace(/dddd/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(now);
        return key;
      });

      // Day (dd = 01-31)
      result = result.replace(/dd/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = day.toString().padStart(2, '0');
        return key;
      });

      // Month (MMMM = full name)
      result = result.replace(/MMMM/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = new Intl.DateTimeFormat(locale, { month: 'long' }).format(now);
        return key;
      });

      // Month (MMM = abbreviated)
      result = result.replace(/MMM/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = new Intl.DateTimeFormat(locale, { month: 'short' }).format(now);
        return key;
      });

      // Month (MM = 01-12)
      result = result.replace(/MM/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = (month + 1).toString().padStart(2, '0');
        return key;
      });

      // Year (yyyy = 4 digits)
      result = result.replace(/yyyy/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = year.toString();
        return key;
      });

      // Year (yy = 2 digits)
      result = result.replace(/yy/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = year.toString().slice(-2);
        return key;
      });

      // Single tokens (d, M, y) - process after multi-character tokens (case-insensitive)
      result = result.replace(/d/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = day.toString();
        return key;
      });

      result = result.replace(/M/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = (month + 1).toString();
        return key;
      });

      result = result.replace(/y/gi, () => {
        const key = `__T${placeholderIndex++}__`;
        placeholders[key] = year.toString().slice(-2);
        return key;
      });

      // Replace placeholders with actual values
      for (const [placeholder, value] of Object.entries(placeholders)) {
        result = result.replace(placeholder, value);
      }

      return result;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  public onApply(): void {
    // Don't proceed if form is invalid
    if (!this.isFormValid()) {
      return;
    }

    let formatValue = '';

    if (this.isDateTime()) {
      const selectedFormat = this.form.controls.selectedFormat.value;
      if (selectedFormat === 'custom') {
        formatValue = this.form.controls.customFormat.value || '';
      } else {
        formatValue = selectedFormat || '';
      }
    } else if (this.isBoolean()) {
      const falseValue = this.form.controls.falseValue.value || '';
      const trueValue = this.form.controls.trueValue.value || '';
      formatValue = `${falseValue}|${trueValue}`;
    } else if (this.isInt32()) {
      formatValue = this.form.controls.decimalPlaces.value?.toString() || '';
    }

    if (!formatValue) {
      return;
    }

    this.applyFormat.emit({ formatValue });
  }
}
